/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

interface WMNumLoadingDone {
  doneLoading: number;
  totalLoading: number;
  found: boolean;
}

export enum WMImageStoreLoadType {
  'PREFETCH' = 'A',
  'GETMAP' = 'B',
}

export default class LoadDurationCounter {
  constructor() {
    this.setLoadingStatusForId = this.setLoadingStatusForId.bind(this);
    this.getTotalLoadingPercentageForIds =
      this.getTotalLoadingPercentageForIds.bind(this);
    this.getLoadDurationForId = this.getLoadDurationForId.bind(this);
    this.setLoadDurationForId = this.setLoadDurationForId.bind(this);
  }
  private _loadingListForIds: Map<string, WMNumLoadingDone> = new Map<
    string,
    WMNumLoadingDone
  >();

  private _loadingDurationForIds: Map<string, number | undefined> = new Map<
    string,
    number
  >();

  setLoadingStatusForId(
    type: WMImageStoreLoadType,
    identifier: string,
    doneLoading: number,
    totalLoading: number,
    resetDuration = false,
  ): void {
    const typeAndIdentifier = `${type}${identifier}`;
    this._loadingListForIds.set(typeAndIdentifier, {
      doneLoading,
      totalLoading,
      found: true,
    });
    if (resetDuration) {
      this._loadingDurationForIds.set(identifier, undefined);
    }
  }

  setLoadDurationForId(identifier: string, duration: number | undefined): void {
    if (duration !== undefined) {
      this._loadingDurationForIds.set(identifier, duration);
    }
  }

  getLoadDurationForId(identifier: string): number | undefined {
    return this._loadingDurationForIds.get(identifier);
  }

  getTotalLoadingPercentageForIds(identifierList: string[]): {
    percentage: number;
    doneLoading: number;
    totalLoading: number;
  } {
    if (!identifierList?.length) {
      return { percentage: 100, doneLoading: 0, totalLoading: 0 };
    }
    // Make list of possible identifiers
    const typeAndIdentifierList: string[] = identifierList.reduce<string[]>(
      (prev: string[], cur: string): string[] => {
        return [
          ...prev,
          `${WMImageStoreLoadType.GETMAP}${cur}`,
          `${WMImageStoreLoadType.PREFETCH}${cur}`,
        ];
      },
      [],
    );
    // Combine all loading images into one number
    const numLoadingDoneTotal = typeAndIdentifierList.reduce<WMNumLoadingDone>(
      (
        previousValue: WMNumLoadingDone,
        identifier: string,
      ): WMNumLoadingDone => {
        const info = this._loadingListForIds.get(identifier);
        return info
          ? {
              doneLoading: info.doneLoading + previousValue.doneLoading,
              totalLoading: info.totalLoading + previousValue.totalLoading,
              found: true,
            }
          : previousValue;
      },
      { doneLoading: 0, totalLoading: 0, found: false },
    );

    // If nothing loading, just return 100 %
    if (numLoadingDoneTotal.totalLoading === 0) {
      return {
        percentage: numLoadingDoneTotal.found ? 0 : 100,
        doneLoading: 0,
        totalLoading: 0,
      };
    }

    // Round upwards to percentages
    return {
      percentage: Math.round(
        (numLoadingDoneTotal.doneLoading / numLoadingDoneTotal.totalLoading) *
          100,
      ),
      doneLoading: numLoadingDoneTotal.doneLoading,
      totalLoading: numLoadingDoneTotal.totalLoading,
    };
  }
}
